import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search, LayoutGrid, List as ListIcon } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { POStatusBadge } from "@/components/POStatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PO_STATUS_LABEL, WAREHOUSE_LABEL } from "@/lib/constants";
import { formatCurrency, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const QUICK_FILTERS = [
  { id: "", label: "Alles" },
  { id: "in_productie", label: "In productie" },
  { id: "onderweg", label: "Onderweg" },
  { id: "deze_week", label: "Deze week verwacht" },
  { id: "vertraagd", label: "Vertraagd" },
  { id: "ontvangen", label: "Ontvangen" },
];

const COLUMN_GROUPS = [
  { id: "draft", title: "Concept" },
  { id: "approved", title: "Goedgekeurd" },
  { id: "ordered", title: "Besteld" },
  { id: "in_production", title: "In productie", also: ["production_planned"] },
  { id: "ready", title: "Gereed" },
  { id: "in_transit", title: "Onderweg", also: ["shipped"] },
  { id: "received", title: "Ontvangen" },
  { id: "delayed", title: "Vertraagd" },
];

export default function Bestellingen() {
  const [params, setParams] = useSearchParams();
  const [pos, setPos] = useState([]);
  const [view, setView] = useState("kanban");
  const [search, setSearch] = useState("");
  const [quick, setQuick] = useState(params.get("quick") || "");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/purchase-orders", { params: { quick_filter: quick || undefined } });
      setPos(r.data);
    } finally { setLoading(false); }
  }, [quick]);

  useEffect(() => {
    load();
    if (quick) setParams({ quick }, { replace: true });
    else setParams({}, { replace: true });
  }, [load, quick, setParams]);

  const filtered = pos.filter((p) =>
    !search || p.po_number.toLowerCase().includes(search.toLowerCase()) ||
    p.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="orders-page">
      <PageHeader
        eyebrow="Inkopen"
        title="Bestellingen"
        description="Alles wat bij producenten en leveranciers openstaat. Klik op een bestelling voor details."
        action={
          <div className="flex items-center gap-2">
            <Button data-testid="view-kanban" variant={view === "kanban" ? "default" : "outline"}
              onClick={() => setView("kanban")} className="h-12 px-4">
              <LayoutGrid className="w-4 h-4 mr-2" /> Pijplijn
            </Button>
            <Button data-testid="view-list" variant={view === "list" ? "default" : "outline"}
              onClick={() => setView("list")} className="h-12 px-4">
              <ListIcon className="w-4 h-4 mr-2" /> Lijst
            </Button>
            <Link to="/bestellingen/nieuw">
              <Button data-testid="new-po" className="h-12 px-4"><Plus className="w-4 h-4 mr-2" /> Nieuwe bestelling</Button>
            </Link>
          </div>
        }
      />

      <div className="surface p-3 md:p-4 mb-4 relative">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input data-testid="po-search" placeholder="Zoek op ordernummer of leverancier…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-11 h-12 text-base border-0 bg-transparent" />
      </div>

      <div className="surface p-4 mb-6 flex flex-wrap gap-2 items-center" data-testid="quick-filter-bar">
        <span className="label-eyebrow">Snelle filters</span>
        {QUICK_FILTERS.map((f) => (
          <Button key={f.id || "all"} data-testid={`quick-${f.id || "all"}`}
            variant={quick === f.id ? "default" : "outline"}
            onClick={() => setQuick(f.id)} size="sm" className="h-10">{f.label}</Button>
        ))}
      </div>

      {view === "kanban" ? <Kanban pos={filtered} loading={loading} /> : <ListView pos={filtered} loading={loading} />}
    </div>
  );
}

const KANBAN_CARD_BORDER = {
  delayed: "border-l-status-critical",
  received: "border-l-status-healthy",
  closed: "border-l-status-healthy",
  draft: "border-l-border",
};

function kanbanBorder(status) {
  return KANBAN_CARD_BORDER[status] || "border-l-status-in-transit";
}

function Kanban({ pos, loading }) {
  return (
    <div className="grid grid-flow-col auto-cols-[320px] gap-4 overflow-x-auto scrollbar-thin pb-4" data-testid="po-kanban">
      {COLUMN_GROUPS.map((col) => {
        const statuses = [col.id, ...(col.also || [])];
        const items = pos.filter((p) => statuses.includes(p.status));
        return (
          <div key={col.id} className="bg-muted/40 rounded-xl p-3" data-testid={`kanban-column-${col.id}`}>
            <div className="flex items-center justify-between px-2 pb-3">
              <div className="text-sm font-semibold tracking-tight">{col.title}</div>
              <span className="text-xs font-mono text-muted-foreground bg-card border border-border rounded-full px-2 py-0.5">
                {items.length}
              </span>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
              {loading && <div className="h-24 bg-card animate-pulse rounded-md" />}
              {items.map((p) => (
                <Link key={p.id} to={`/bestellingen/${p.id}`} data-testid={`kanban-card-${p.po_number}`}
                  className={cn("block bg-card border border-border rounded-md p-4 hover:border-primary/50 transition-colors border-l-4",
                    kanbanBorder(p.status))}>
                  <div className="font-mono text-sm font-semibold">{p.po_number}</div>
                  <div className="text-sm text-muted-foreground truncate mt-1">{p.supplier_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">→ {WAREHOUSE_LABEL[p.receive_warehouse] || "—"}</div>
                  <div className="flex items-center justify-between mt-3 text-xs">
                    <span className="text-muted-foreground">{p.lines?.length || 0} regels · {formatDate(p.expected_delivery_date)}</span>
                    <span className="font-mono font-semibold">{formatCurrency(p.total_value)}</span>
                  </div>
                </Link>
              ))}
              {!loading && items.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">Geen bestellingen</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ pos, loading }) {
  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-base">
          <thead className="bg-muted/50 text-left text-sm uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-4">Ordernummer</th>
              <th className="px-4 py-4">Leverancier</th>
              <th className="px-4 py-4">Status</th>
              <th className="px-4 py-4">Besteldatum</th>
              <th className="px-4 py-4">Productie</th>
              <th className="px-4 py-4">Verwacht</th>
              <th className="px-4 py-4">Magazijn</th>
              <th className="px-4 py-4 text-right">Regels</th>
              <th className="px-4 py-4 text-right">Waarde</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? Array.from({ length: 6 }).map((_, i) => (
              <tr key={`sk-${i}`}><td colSpan={9} className="px-4 py-5"><div className="h-10 bg-muted/40 animate-pulse rounded-md" /></td></tr>
            )) : pos.map((p) => (
              <tr key={p.id} className="hover:bg-accent transition-colors" data-testid={`po-row-${p.po_number}`}>
                <td className="px-4 py-4"><Link to={`/bestellingen/${p.id}`} className="font-mono font-semibold hover:text-primary">{p.po_number}</Link></td>
                <td className="px-4 py-4">{p.supplier_name}</td>
                <td className="px-4 py-4"><POStatusBadge status={p.status} /></td>
                <td className="px-4 py-4 text-muted-foreground">{formatDate(p.order_date)}</td>
                <td className="px-4 py-4 text-muted-foreground">{formatDate(p.production_date)}</td>
                <td className="px-4 py-4 text-muted-foreground">{formatDate(p.expected_delivery_date)}</td>
                <td className="px-4 py-4 text-sm">{WAREHOUSE_LABEL[p.receive_warehouse] || "—"}</td>
                <td className="px-4 py-4 text-right font-mono">{p.lines?.length || 0}</td>
                <td className="px-4 py-4 text-right font-mono font-semibold">{formatCurrency(p.total_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
